import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Message, SelectPicker } from 'rsuite';
import DatePicker from '../components/shared/DatePicker';
import { useNavigate, useParams } from 'react-router-dom';
import { showToast } from '../utils/toast';
import LoadingSpinner from '../components/LoadingSpinner';
import ProfilePhoto from '../components/employees/ProfilePhoto';
import PayRateHistory from '../components/employees/PayRateHistory';
import CommitmentHistory, { currentCommitment } from '../components/employees/CommitmentHistory';
import SharePointLocationPicker from '../components/employees/SharePointLocationPicker';
import DocumentLibraryBrowser from '../components/employees/DocumentLibraryBrowser';
import ProfileField, { ProfileValue } from '../components/employees/ProfileField';
import RetirementElection, { RetirementUsageSummary } from '../components/payroll/RetirementElection';
import { PAYROLL_ENABLED } from '../config/payrollFeature';
import PatoBalance, { PatoUsageSummary } from '../components/payroll/PatoBalance';
import PatoReportModal from '../components/payroll/PatoReportModal';
import HoursMeasureLabel from '../components/shared/HoursMeasureLabel';
import CopyTextButton from '../components/shared/CopyTextButton';
import { useAuth } from '../contexts/AuthContext';
import { useCoachOptions, useCommitmentHistory, useEmployee, useUpdateEmployee } from '../hooks/useQueries';
import { canEditField } from '../utils/permissionUtils';
import {
  EMERGENCY_RELATIONSHIP_OPTIONS,
  MONTHS,
  addressLocality,
  daysInMonth,
  digitsOnly,
  emergencyRelationshipLabel,
  employmentTenure,
  formatBirthday,
  formatCombinedAddress,
  formatCurrency,
  formatDateDisplay,
  formatPercent,
  formatPhone,
  formatPhoneForDisplay,
  normalizeState,
  normalizeZip,
  parseAddressString,
  parseLocalDateOnly,
  parseMonthDay,
  toBirthdayDateString,
  toDateInputValue,
} from '../utils/profileFormat';
import './MePage.css';

// Number and extension read as one thing rather than two rows. Either half can
// stand alone: a shared office line is worth showing before anyone has been
// given an extension, and an extension is worth showing to someone who only
// knows the main number.
const formatOfficePhoneForDisplay = (employee) => {
  const number = formatPhoneForDisplay(employee?.office_phone);
  const extension = String(employee?.office_phone_extension || '').trim();
  if (number && extension) return `${number} x${extension}`;
  if (number) return number;
  return extension ? `Ext. ${extension}` : '';
};

// The form mirrors the record as strings, so a draft can hold a half-typed
// phone number or a month without a day without corrupting the employee object.
const buildForm = (employee) => {
  const address = parseAddressString(employee?.address);
  const { month, day } = parseMonthDay(employee?.birthday);

  return {
    email: employee?.email || '',
    phone: formatPhone(employee?.phone),
    officePhone: formatPhone(employee?.office_phone),
    officePhoneExtension: employee?.office_phone_extension || '',
    street: address.street,
    city: address.city,
    state: address.state,
    zip: address.zip,
    birthdayMonth: month ? String(month) : '',
    birthdayDay: day ? String(day) : '',
    shareBirthday: Boolean(employee?.share_birthday_during_meetings),
    jobTitle: employee?.job_title || '',
    coachId: employee?.coach_id || '',
    hireDate: toDateInputValue(employee?.hire_date),
    emergencyName: employee?.emergency_contact_name || '',
    emergencyPhone: formatPhone(employee?.emergency_contact_phone),
    emergencyRelationship: employee?.emergency_contact_relationship || '',
    timecampUserId: employee?.timecamp_user_id || '',
    sharepointDriveId: employee?.sharepoint_drive_id || '',
  };
};

export default function MePage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const { id: routeEmployeeId } = useParams();

  const isRouteEditingEmployee = Boolean(routeEmployeeId);
  const isAdminEditingEmployee = auth.isAdmin() && isRouteEditingEmployee;
  const canManageAllEditingEmployee = isRouteEditingEmployee
    && (
      auth.isAdmin()
      || auth.hasPermission('manage_all_employees')
    );
  const canManageEmployeeRecord = isRouteEditingEmployee
    && (
      auth.isAdmin()
      || auth.hasPermission('manage_all_employees')
      || auth.hasPermission('manage_coached_employees')
    );
  const canManageDocumentLibrary = canManageEmployeeRecord;

  const employeeQuery = useEmployee(routeEmployeeId, { enabled: isRouteEditingEmployee });
  const coachOptionsQuery = useCoachOptions({ enabled: canManageAllEditingEmployee });
  const updateEmployee = useUpdateEmployee();

  const employee = isRouteEditingEmployee ? (employeeQuery.data || null) : (auth.employee || null);

  const coachOptions = useMemo(() => {
    const options = coachOptionsQuery.data || [];
    if (!employee?.id) return options;
    return options.filter((o) => o?.value !== employee.id);
  }, [coachOptionsQuery.data, employee?.id]);

  const currentUser = useMemo(
    () => ({ role: auth.role || null, employee: auth.employee || null }),
    [auth.role, auth.employee]
  );

  const canEdit = (fieldKey) => {
    if (isAdminEditingEmployee) return true;
    return canEditField(currentUser, fieldKey);
  };

  // Who may change what. Everything below reads from this rather than
  // re-deriving a permission at the point of use.
  const perms = {
    email: isAdminEditingEmployee,
    phone: canEdit('phone'),
    officePhone: canEdit('office_phone'),
    address: canEdit('address'),
    birthday: canEdit('birthday'),
    shareBirthday: canEdit('share_birthday_during_meetings'),
    jobTitle: canEdit('job_title'),
    emergency: canEdit('emergency_contact_name'),
    coach: canManageAllEditingEmployee,
    hireDate: canManageEmployeeRecord,
    employment: canManageEmployeeRecord,
    timecamp: isAdminEditingEmployee,
    documentLibrary: canManageDocumentLibrary,
  };
  const canEditAnything = Object.values(perms).some(Boolean);

  // Pay rates are their own record, edited through PayRateHistory's modal rather
  // than the profile form — so the Compensation card head drives that component
  // through a handle instead of owning the state.
  //
  // On your own profile only manage_all_employees may change the rate. A coach
  // manages their coachees, not themselves, and the server refuses their own
  // rate. Without that permission the history is given no employee id, so it
  // reads the read-only /me history and offers no edits; with one, the rule
  // below mirrors the one PayRateHistory applies internally.
  const payRatesRef = useRef(null);
  const canManageOwnPayRates = !isRouteEditingEmployee
    && (auth.isAdmin() || auth.hasPermission('manage_all_employees'));
  const payRatesEmployeeId = isRouteEditingEmployee || canManageOwnPayRates
    ? employee?.id
    : undefined;
  const canManagePayRates = Boolean(payRatesEmployeeId)
    && (
      auth.isAdmin()
      || auth.hasPermission('manage_all_employees')
      || auth.hasPermission('manage_coached_employees')
    );

  // A retirement deferral is the employee's own election, so they set it on
  // their own profile without needing a payroll permission - the server applies
  // the same rule, matched on the id in the path.
  //
  // A coach managing somebody else's record is deliberately not included: they
  // can see the record, but somebody else's deferral is not theirs to change,
  // and the server would refuse the write anyway.
  const isOwnProfile = !isRouteEditingEmployee;
  const canManageRetirement = isOwnProfile || canManageAllEditingEmployee;
  const canSeeRetirement = PAYROLL_ENABLED
    && Boolean(employee?.id)
    && canManageRetirement;

  // The PATO and Retirement section of the compensation card. Closed by default.
  const [isPayExtrasOpen, setIsPayExtrasOpen] = useState(false);

  // The full PATO ledger is a tool for whoever manages this record, so it is
  // offered on the admin view of somebody's profile and never on /me - the
  // same people the server lets read another employee's PATO.
  const canViewPatoReport = canManageAllEditingEmployee;
  const [isPatoReportOpen, setIsPatoReportOpen] = useState(false);

  const baseline = useMemo(() => buildForm(employee), [employee]);
  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState(baseline);
  const [errors, setErrors] = useState({});
  const [saveError, setSaveError] = useState(null);

  // A profile loaded (or refetched) under the form resets it — but only while
  // the user is not mid-edit, where it would discard what they typed.
  useEffect(() => {
    if (isEditing) return;
    setForm(baseline);
  }, [baseline, isEditing]);

  const saving = updateEmployee.isPending;

  /*
    What this person is measured against today, read from their dated
    commitment history - the same query (and cache) the history list under the
    Employment card uses - rather than the employee record's mirror columns,
    which only catch up on a change dated ahead overnight. The record is the
    fallback for somebody with no history yet.
  */
  const commitmentsEmployeeId = isRouteEditingEmployee ? employee?.id : undefined;
  const commitmentsQuery = useCommitmentHistory(commitmentsEmployeeId || 'me', { enabled: Boolean(employee) });
  const todaysCommitment = currentCommitment(commitmentsQuery.data);
  const isNonExempt = todaysCommitment
    ? Boolean(todaysCommitment.is_part_time)
    : Boolean(employee?.is_part_time);
  const currentTawhPercent = todaysCommitment ? todaysCommitment.tawh_percent : employee?.tawh_percent;
  const currentTargetHours = todaysCommitment ? todaysCommitment.target_hours : employee?.target_hours;

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));
  };

  const startEdit = () => {
    setForm(baseline);
    setErrors({});
    setSaveError(null);
    setIsEditing(true);
  };

  const cancelEdit = () => {
    setForm(baseline);
    setErrors({});
    setSaveError(null);
    setIsEditing(false);
  };

  const isDirty = useMemo(
    () => Object.keys(baseline).some((key) => form[key] !== baseline[key]),
    [form, baseline]
  );

  const changed = (...keys) => keys.some((key) => form[key] !== baseline[key]);

  // Collects the changed, permitted fields into one PUT body. Validation
  // failures are returned as a field->message map rather than thrown, so every
  // problem on the page is shown at once instead of one per save attempt.
  const buildPayload = () => {
    const payload = {};
    const nextErrors = {};

    if (perms.email && changed('email')) {
      const email = form.email.trim();
      if (!email) nextErrors.email = 'Email is required';
      else payload.email = email;
    }

    if (perms.phone && changed('phone')) {
      const d = digitsOnly(form.phone);
      if (d && d.length !== 10) nextErrors.phone = 'Enter a 10-digit phone number';
      else payload.phone = d || null;
    }

    if (perms.officePhone) {
      if (changed('officePhone')) {
        const d = digitsOnly(form.officePhone);
        if (d && d.length !== 10) nextErrors.officePhone = 'Enter a 10-digit phone number';
        else payload.office_phone = d || null;
      }
      // The extension stands on its own rather than being cleared with the
      // number: most people share one office line, and someone who has only
      // been told their extension can still record it.
      if (changed('officePhoneExtension')) {
        const d = digitsOnly(form.officePhoneExtension);
        if (d.length > 10) nextErrors.officePhoneExtension = 'Extensions are at most 10 digits';
        else payload.office_phone_extension = d || null;
      }
    }

    if (perms.address && changed('street', 'city', 'state', 'zip')) {
      const street = form.street.trim();
      const city = form.city.trim();
      const state = normalizeState(form.state);
      const zip = normalizeZip(form.zip);

      if (!street && !city && !state && !zip) {
        payload.address = null;
      } else if (!street || !city || !state || !zip) {
        nextErrors.address = 'Street, city, state, and ZIP are all required';
      } else if (state.length !== 2) {
        nextErrors.address = 'State must be 2 letters';
      } else if (zip.length !== 5) {
        nextErrors.address = 'ZIP must be 5 digits';
      } else {
        payload.address = formatCombinedAddress({ street, city, state, zip });
      }
    }

    if (perms.birthday && changed('birthdayMonth', 'birthdayDay')) {
      const hasMonth = Boolean(form.birthdayMonth);
      const hasDay = Boolean(form.birthdayDay);

      if (!hasMonth && !hasDay) {
        payload.birthday = null;
      } else if (!hasMonth || !hasDay) {
        nextErrors.birthday = 'Pick both a month and a day';
      } else {
        const birthday = toBirthdayDateString(form.birthdayMonth, form.birthdayDay);
        if (!birthday) nextErrors.birthday = 'That day does not exist in that month';
        else payload.birthday = birthday;
      }
    }

    if (perms.shareBirthday && changed('shareBirthday')) {
      payload.share_birthday_during_meetings = Boolean(form.shareBirthday);
    }

    if (perms.jobTitle && changed('jobTitle')) {
      payload.job_title = form.jobTitle.trim() || null;
    }

    if (perms.emergency) {
      if (changed('emergencyName')) {
        payload.emergency_contact_name = form.emergencyName.trim() || null;
      }
      if (changed('emergencyPhone')) {
        const d = digitsOnly(form.emergencyPhone);
        if (d && d.length !== 10) nextErrors.emergencyPhone = 'Enter a 10-digit phone number';
        else payload.emergency_contact_phone = d || null;
      }
      if (changed('emergencyRelationship')) {
        payload.emergency_contact_relationship = form.emergencyRelationship || null;
      }
    }

    if (perms.coach && changed('coachId')) {
      payload.coach_id = form.coachId || null;
    }

    if (perms.hireDate && changed('hireDate')) {
      payload.hire_date = form.hireDate || null;
    }

    if (perms.timecamp && changed('timecampUserId')) {
      payload.timecamp_user_id = form.timecampUserId.trim() || null;
    }

    if (perms.documentLibrary && changed('sharepointDriveId')) {
      payload.sharepoint_drive_id = form.sharepointDriveId || null;
      payload.document_library_id = form.sharepointDriveId || null;
    }

    return { payload, nextErrors };
  };

  const save = async () => {
    if (!employee?.id || saving) return;

    setSaveError(null);
    const { payload, nextErrors } = buildPayload();

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      showToast('error', 'Fix the highlighted fields before saving');
      return;
    }

    setErrors({});

    if (Object.keys(payload).length === 0) {
      setIsEditing(false);
      return;
    }

    try {
      await updateEmployee.mutateAsync({ id: employee.id, ...payload });
      setIsEditing(false);
      showToast('success', 'Profile updated');
    } catch (err) {
      const message = String(err?.message || err);
      setSaveError(message);
      showToast('error', message);
    }
  };

  /* ------------------------------------------------------------- gating --- */

  if (auth.isLoading) {
    return <LoadingSpinner message="Loading profile..." />;
  }

  if (auth.error) {
    return (
      <Message type="error" header="Error" showIcon>
        {String(auth.error?.message || auth.error)}
      </Message>
    );
  }

  if (isRouteEditingEmployee) {
    if (employeeQuery.isLoading) {
      return <LoadingSpinner message="Loading employee..." />;
    }
    if (employeeQuery.error) {
      return (
        <Message type="error" header="Error" showIcon>
          {String(employeeQuery.error?.message || employeeQuery.error)}
        </Message>
      );
    }
  }

  /* ------------------------------------------------------ derived values --- */

  const fullName = `${employee?.first_name || ''} ${employee?.last_name || ''}`.trim() || 'Employee';
  const initials = `${String(employee?.first_name || '').trim().slice(0, 1)}${String(employee?.last_name || '').trim().slice(0, 1)}`.toUpperCase() || 'U';
  const photoEmployeeId = isRouteEditingEmployee ? routeEmployeeId : 'me';
  const resumePath = isRouteEditingEmployee ? `/admin/employees/${routeEmployeeId}/resume` : '/me/resume';
  const canViewResume = !isRouteEditingEmployee || canManageEmployeeRecord;

  const tenure = employmentTenure(employee?.hire_date);
  const locality = addressLocality(employee?.address);
  const currentRate = employee?.current_pay_rate ?? employee?.pay_rate;
  const copyEmail = isEditing && perms.email ? form.email : employee?.email;
  const copyPhone = isEditing && perms.phone ? form.phone : formatPhoneForDisplay(employee?.phone);
  const copyAddress = isEditing && perms.address
    ? formatCombinedAddress({ street: form.street, city: form.city, state: form.state, zip: form.zip })
    : formatCombinedAddress(parseAddressString(employee?.address));
  const dayOptions = Array.from(
    { length: daysInMonth(form.birthdayMonth || 1) },
    (_v, idx) => idx + 1
  );

  const subtitle = [employee?.job_title, employee?.role_name].filter(Boolean).join(' · ');

  /* -------------------------------------------------------------- render --- */

  return (
    <div className="me-page">
      <header className="me-hero">
        <div className="me-hero__identity">
          <div className="me-hero__photo">
            <ProfilePhoto employeeId={photoEmployeeId} size="medium" fallbackInitials={initials} />
          </div>

          <div className="me-hero__meta">
            <div className="me-hero__nameRow">
              <h1 className="me-hero__name">{fullName}</h1>
              <span className={`me-pill ${employee?.is_active === false ? 'me-pill--warn' : 'me-pill--ok'}`}>
                {employee?.is_active === false ? 'Inactive' : 'Active'}
              </span>
              {isNonExempt ? <span className="me-pill me-pill--muted">Non-exempt</span> : null}
            </div>

            <p className="me-hero__role">{subtitle || 'No job title set'}</p>

            <div className="me-hero__facts">
              {copyEmail ? (
                <CopyTextButton value={copyEmail} label="email" className="me-hero__fact me-hero__copyfact">
                  <i className="bi bi-envelope" aria-hidden="true" />
                  <span>{copyEmail}</span>
                </CopyTextButton>
              ) : (
                <span className="me-hero__fact">
                  <i className="bi bi-envelope" aria-hidden="true" />
                  <span>—</span>
                </span>
              )}
              {employee?.phone ? (
                <CopyTextButton value={copyPhone} label="phone number" className="me-hero__fact me-hero__copyfact">
                  <i className="bi bi-telephone" aria-hidden="true" />
                  <span>{copyPhone}</span>
                </CopyTextButton>
              ) : null}
              {copyAddress ? (
                <CopyTextButton value={copyAddress} label="address" className="me-hero__fact me-hero__copyfact">
                  <i className="bi bi-geo-alt" aria-hidden="true" />
                  <span>{locality || copyAddress}</span>
                </CopyTextButton>
              ) : null}
              {employee?.coach_name ? (
                <span className="me-hero__fact">
                  <i className="bi bi-person-badge" aria-hidden="true" />
                  <span>Coach: {employee.coach_name}</span>
                </span>
              ) : null}
            </div>
          </div>
        </div>

        <div className="me-hero__actions">
          {!isEditing ? (
            <>
              {canViewResume ? (
                <button type="button" className="bdc-btn" onClick={() => navigate(resumePath)}>
                  <i className="bi bi-file-earmark-person" aria-hidden="true" />
                  Resume
                </button>
              ) : null}
              {canEditAnything ? (
                <button type="button" className="bdc-btn bdc-btn--primary" onClick={startEdit}>
                  <i className="bi bi-pencil" aria-hidden="true" />
                  Edit profile
                </button>
              ) : null}
            </>
          ) : (
            <>
              <button type="button" className="bdc-btn" onClick={cancelEdit} disabled={saving}>
                Cancel
              </button>
              <button type="button" className="bdc-btn bdc-btn--primary" onClick={save} disabled={saving}>
                <i className="bi bi-check2" aria-hidden="true" />
                {saving ? 'Saving…' : 'Save changes'}
              </button>
            </>
          )}
        </div>
      </header>

      {isEditing ? (
        <div className="me-banner">
          <i className="bi bi-pencil-square" aria-hidden="true" />
          <span>
            Editing this profile. Nothing is stored until you save — fields with a padlock are not yours to change here.
          </span>
        </div>
      ) : null}

      {saveError ? (
        <div className="me-banner me-banner--error">
          <i className="bi bi-exclamation-triangle" aria-hidden="true" />
          <span>{saveError}</span>
        </div>
      ) : null}

      <div className="me-layout">
        <aside className="me-col">
          {/* ------------------------------------------------------ personal */}
          <section className="me-card">
            <div className="me-card__head">
              <h2 className="me-card__title">
                <i className="bi bi-person" aria-hidden="true" />
                Personal
              </h2>
            </div>

            <div className="me-card__body">
              <ProfileField
                label="Legal name"
                locked
                showLock={isEditing}
                hint={isEditing ? 'From your Microsoft account' : null}
              >
                <ProfileValue value={fullName} strong />
              </ProfileField>

              <ProfileField
                label="Email"
                locked={!perms.email}
                showLock={isEditing}
                error={errors.email}
                hint={isEditing && !perms.email ? 'Managed by your Microsoft account' : null}
              >
                {isEditing && perms.email ? (
                  <input
                    className={`me-input${errors.email ? ' me-input--invalid' : ''}`}
                    type="email"
                    aria-label="Email"
                    value={form.email}
                    onChange={(e) => setField('email', e.target.value)}
                    disabled={saving}
                  />
                ) : (
                  <ProfileValue value={employee?.email} />
                )}
              </ProfileField>

              <ProfileField
                label="Phone"
                locked={!perms.phone}
                showLock={isEditing}
                error={errors.phone}
              >
                {isEditing && perms.phone ? (
                  <input
                    className={`me-input${errors.phone ? ' me-input--invalid' : ''}`}
                    type="tel"
                    inputMode="tel"
                    placeholder="(555) 555-5555"
                    aria-label="Phone"
                    value={form.phone}
                    onChange={(e) => setField('phone', formatPhone(e.target.value))}
                    disabled={saving}
                  />
                ) : (
                  <ProfileValue value={formatPhoneForDisplay(employee?.phone)} />
                )}
              </ProfileField>

              <ProfileField
                label="Office phone"
                locked={!perms.officePhone}
                showLock={isEditing}
                error={errors.officePhone || errors.officePhoneExtension}
              >
                {isEditing && perms.officePhone ? (
                  <div className="me-inputrow me-inputrow--split">
                    <input
                      className={`me-input${errors.officePhone ? ' me-input--invalid' : ''}`}
                      type="tel"
                      inputMode="tel"
                      placeholder="(555) 555-5555"
                      aria-label="Office phone"
                      value={form.officePhone}
                      onChange={(e) => setField('officePhone', formatPhone(e.target.value))}
                      disabled={saving}
                    />
                    <input
                      className={`me-input${errors.officePhoneExtension ? ' me-input--invalid' : ''}`}
                      type="text"
                      inputMode="numeric"
                      placeholder="Ext."
                      aria-label="Office phone extension"
                      value={form.officePhoneExtension}
                      onChange={(e) => setField('officePhoneExtension', digitsOnly(e.target.value).slice(0, 10))}
                      disabled={saving}
                    />
                  </div>
                ) : (
                  <ProfileValue value={formatOfficePhoneForDisplay(employee)} />
                )}
              </ProfileField>

              <ProfileField
                label="Birthday"
                locked={!perms.birthday}
                showLock={isEditing}
                error={errors.birthday}
                hint={!isEditing ? null : 'The year is never stored.'}
              >
                {isEditing && perms.birthday ? (
                  <div className="me-inputrow me-inputrow--split">
                    <select
                      className="me-select"
                      aria-label="Birthday month"
                      value={form.birthdayMonth}
                      disabled={saving}
                      onChange={(e) => {
                        const month = e.target.value;
                        setField('birthdayMonth', month);
                        // February can't hold a 30th — drop a day the new month
                        // doesn't have rather than saving an invalid date.
                        if (month && Number(form.birthdayDay) > daysInMonth(month)) {
                          setField('birthdayDay', '');
                        }
                      }}
                    >
                      <option value="">Month</option>
                      {MONTHS.map((label, idx) => (
                        <option key={label} value={String(idx + 1)}>{label}</option>
                      ))}
                    </select>

                    <select
                      className="me-select"
                      aria-label="Birthday day"
                      value={form.birthdayDay}
                      disabled={saving}
                      onChange={(e) => setField('birthdayDay', e.target.value)}
                    >
                      <option value="">Day</option>
                      {dayOptions.map((d) => (
                        <option key={d} value={String(d)}>{d}</option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <ProfileValue value={formatBirthday(employee?.birthday)} />
                )}
              </ProfileField>

              <ProfileField label="Birthday in meetings" locked={!perms.shareBirthday} showLock={isEditing}>
                {isEditing && perms.shareBirthday ? (
                  <label className="me-check">
                    <input
                      type="checkbox"
                      checked={form.shareBirthday}
                      disabled={saving}
                      onChange={(e) => setField('shareBirthday', e.target.checked)}
                    />
                    <span>Ok to share my birthday during meetings</span>
                  </label>
                ) : (
                  <ProfileValue value={employee?.share_birthday_during_meetings ? 'Ok to share' : 'Not shared'} />
                )}
              </ProfileField>

              <ProfileField
                label="Address"
                locked={!perms.address}
                showLock={isEditing}
                error={errors.address}
              >
                {isEditing && perms.address ? (
                  <div className="d-flex flex-column gap-2">
                    <input
                      className={`me-input${errors.address ? ' me-input--invalid' : ''}`}
                      placeholder="Street"
                      aria-label="Street"
                      value={form.street}
                      onChange={(e) => setField('street', e.target.value)}
                      disabled={saving}
                    />
                    <div className="me-inputrow">
                      <input
                        className={`me-input${errors.address ? ' me-input--invalid' : ''}`}
                        placeholder="City"
                        aria-label="City"
                        value={form.city}
                        onChange={(e) => setField('city', e.target.value)}
                        disabled={saving}
                      />
                      <input
                        className={`me-input${errors.address ? ' me-input--invalid' : ''}`}
                        placeholder="State"
                        aria-label="State"
                        value={form.state}
                        onChange={(e) => setField('state', normalizeState(e.target.value))}
                        disabled={saving}
                      />
                      <input
                        className={`me-input${errors.address ? ' me-input--invalid' : ''}`}
                        placeholder="ZIP"
                        aria-label="ZIP"
                        inputMode="numeric"
                        value={form.zip}
                        onChange={(e) => setField('zip', normalizeZip(e.target.value))}
                        disabled={saving}
                      />
                    </div>
                  </div>
                ) : (
                  <ProfileValue value={formatCombinedAddress(parseAddressString(employee?.address))} />
                )}
              </ProfileField>
            </div>
          </section>

          {/* --------------------------------------------- emergency contact */}
          <section className="me-card">
            <div className="me-card__head">
              <h2 className="me-card__title">
                <i className="bi bi-heart-pulse" aria-hidden="true" />
                Emergency contact
              </h2>
            </div>

            <div className="me-card__body">
              <ProfileField label="Name" locked={!perms.emergency} showLock={isEditing}>
                {isEditing && perms.emergency ? (
                  <input
                    className="me-input"
                    aria-label="Emergency contact name"
                    value={form.emergencyName}
                    onChange={(e) => setField('emergencyName', e.target.value)}
                    disabled={saving}
                  />
                ) : (
                  <ProfileValue value={employee?.emergency_contact_name} strong />
                )}
              </ProfileField>

              <ProfileField label="Relationship" locked={!perms.emergency} showLock={isEditing}>
                {isEditing && perms.emergency ? (
                  <select
                    className="me-select"
                    aria-label="Emergency contact relationship"
                    value={form.emergencyRelationship}
                    onChange={(e) => setField('emergencyRelationship', e.target.value)}
                    disabled={saving}
                  >
                    <option value="">Select relationship</option>
                    {EMERGENCY_RELATIONSHIP_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                ) : (
                  <ProfileValue value={emergencyRelationshipLabel(employee?.emergency_contact_relationship)} />
                )}
              </ProfileField>

              <ProfileField
                label="Phone"
                locked={!perms.emergency}
                showLock={isEditing}
                error={errors.emergencyPhone}
              >
                {isEditing && perms.emergency ? (
                  <input
                    className={`me-input${errors.emergencyPhone ? ' me-input--invalid' : ''}`}
                    type="tel"
                    inputMode="tel"
                    placeholder="(555) 555-5555"
                    aria-label="Emergency contact phone"
                    value={form.emergencyPhone}
                    onChange={(e) => setField('emergencyPhone', formatPhone(e.target.value))}
                    disabled={saving}
                  />
                ) : (
                  <ProfileValue value={formatPhoneForDisplay(employee?.emergency_contact_phone)} />
                )}
              </ProfileField>
            </div>
          </section>
        </aside>

        <div className="me-col">
          <div className="me-row">
            {/* ---------------------------------------------------- employment */}
            <section className="me-card">
              <div className="me-card__head">
                <h2 className="me-card__title">
                  <i className="bi bi-briefcase" aria-hidden="true" />
                  Employment
                </h2>
              </div>

              <div className="me-card__body">
                <div className="me-tenure">
                  <div className="me-tenure__head">
                    <span className="me-field__label">Tenure</span>
                    <span className={`me-pill ${isNonExempt ? 'me-pill--muted' : 'me-pill--ok'}`}>
                      {isNonExempt ? 'Non-exempt' : 'Exempt'}
                    </span>
                  </div>

                  {tenure ? (
                    <div className="me-tenure__value bdc-num">
                      <span className="me-tenure__num">{tenure.years}</span>
                      <span className="me-tenure__unit">{tenure.years === 1 ? 'yr' : 'yrs'}</span>
                      <span className="me-tenure__num">{tenure.months}</span>
                      <span className="me-tenure__unit">{tenure.months === 1 ? 'mo' : 'mos'}</span>
                    </div>
                  ) : (
                    <div className="me-tenure__value">
                      <span className="me-tenure__unit">Not available without a hire date</span>
                    </div>
                  )}

                  <div className="me-tenure__foot">
                    <i className="bi bi-calendar-event" aria-hidden="true" />
                    <span>
                      Hired:{' '}
                      <strong className="bdc-num">{formatDateDisplay(employee?.hire_date) || 'Not set'}</strong>
                    </span>
                  </div>
                </div>

                <div className="me-fieldgrid">
                  <ProfileField label="Job title" locked={!perms.jobTitle} showLock={isEditing}>
                    {isEditing && perms.jobTitle ? (
                      <input
                        className="me-input"
                        aria-label="Job title"
                        value={form.jobTitle}
                        onChange={(e) => setField('jobTitle', e.target.value)}
                        disabled={saving}
                      />
                    ) : (
                      <ProfileValue value={employee?.job_title} strong />
                    )}
                  </ProfileField>

                  <ProfileField label="Coach" locked={!perms.coach} showLock={isEditing}>
                    {isEditing && perms.coach ? (
                      <SelectPicker
                        className="me-picker"
                        aria-label="Coach"
                        data={coachOptions}
                        value={form.coachId === '' ? null : form.coachId}
                        onChange={(value) => setField('coachId', value ?? '')}
                        disabled={saving}
                        searchable
                        cleanable
                        placeholder="No coach"
                        block
                      />
                    ) : (
                      <ProfileValue value={employee?.coach_name} />
                    )}
                  </ProfileField>

                  <ProfileField label="Hire date" locked={!perms.hireDate} showLock={isEditing}>
                    {isEditing && perms.hireDate ? (
                      <DatePicker
                        className="me-picker"
                        aria-label="Hire date"
                        value={parseLocalDateOnly(form.hireDate)}
                        onChange={(value) => setField('hireDate', toDateInputValue(value))}
                        disabled={saving}
                        oneTap
                        cleanable
                        isoWeek={false}
                        weekStart={0}
                        format="MM/dd/yyyy"
                        placeholder="mm/dd/yyyy"
                        block
                      />
                    ) : (
                      <ProfileValue value={formatDateDisplay(employee?.hire_date)} mono />
                    )}
                  </ProfileField>

                  <ProfileField label="Access role" locked showLock={isEditing}>
                    <ProfileValue value={employee?.role_name} />
                  </ProfileField>

                  {/* One row, and which measure it holds follows the
                      classification: exempt employees are held to a percentage
                      of their available hours, non-exempt to a number of hours
                      a pay period. Nobody has both. Read-only here - the
                      commitment is dated, and changes go through the history
                      below so each says which pay period it starts from. */}
                  <ProfileField
                    label={<HoursMeasureLabel nonExempt={isNonExempt} />}
                    locked
                    showLock={isEditing}
                    hint={isEditing && perms.employment ? 'Change it under Commitment history' : undefined}
                  >
                    <ProfileValue
                      value={
                        isNonExempt
                          ? (currentTargetHours == null
                            ? ''
                            : `${Number(currentTargetHours)} hrs`)
                          : formatPercent(currentTawhPercent)
                      }
                      mono
                    />
                  </ProfileField>

                  <ProfileField label="Classification" locked showLock={isEditing}>
                    <ProfileValue value={isNonExempt ? 'Non-exempt' : 'Exempt'} />
                  </ProfileField>

                  {perms.timecamp ? (
                    <ProfileField label="TimeCamp user ID" full>
                      {isEditing ? (
                        <input
                          className="me-input"
                          aria-label="TimeCamp user ID"
                          value={form.timecampUserId}
                          onChange={(e) => setField('timecampUserId', e.target.value)}
                          disabled={saving}
                        />
                      ) : (
                        <ProfileValue value={employee?.timecamp_user_id} mono />
                      )}
                    </ProfileField>
                  ) : null}
                </div>

                <div className="me-divider" />

                {/* What this person is measured against, by pay period. Every
                    past period is judged against the commitment in force then,
                    so moving somebody up or down does not re-mark old ones. */}
                <CommitmentHistory
                  employeeId={commitmentsEmployeeId}
                  canManage={perms.employment}
                />
              </div>
            </section>

            {/* -------------------------------------------------- compensation */}
            <section className="me-card">
              <div className="me-card__head">
                <h2 className="me-card__title">
                  <i className="bi bi-cash-coin" aria-hidden="true" />
                  Compensation
                </h2>

                {canManagePayRates ? (
                  <button
                    type="button"
                    className="bdc-btn bdc-btn--sm"
                    onClick={() => payRatesRef.current?.openAdd()}
                  >
                    <i className="bi bi-plus-lg" aria-hidden="true" />
                    Add new rate
                  </button>
                ) : null}
              </div>

              <div className="me-card__body">
                <div className="me-rateRow">
                  <div className="me-field">
                    <div className="me-field__label">Current pay rate</div>
                    {currentRate == null || currentRate === '' ? (
                      <ProfileValue value="" />
                    ) : (
                      <div className="me-rate bdc-num">
                        <span className="me-rate__amount">{formatCurrency(currentRate)}</span>
                        <span className="me-rate__unit">/hr</span>
                      </div>
                    )}
                  </div>

                  {employee?.current_pay_rate_effective_date ? (
                    <div className="me-rate__note">
                      Effective {formatDateDisplay(employee.current_pay_rate_effective_date)}
                    </div>
                  ) : null}
                </div>

                <div className="me-divider" />

                <PayRateHistory
                  ref={payRatesRef}
                  employeeId={payRatesEmployeeId}
                  hireDate={employee?.hire_date}
                  embedded
                  showAddButton={false}
                />

                {/* Retirement elections belong with the rate they are
                    calculated against. Shown to the employee on their own
                    profile - the deferral is their decision to make - and to an
                    admin editing somebody's record. Part of the payroll module;
                    deleting this block and the import removes it. */}
                {canSeeRetirement ? (
                  <>
                    <div className="me-divider" />
                    {/* Folded away by default: both are looked at now and then,
                        not every visit, and open they push the rest of the card
                        a long way down. Kept mounted while closed so opening it
                        shows figures rather than a loading line, and `inert`
                        keeps the hidden controls out of the tab order. */}
                    <div className={`me-disclosure${isPayExtrasOpen ? ' is-open' : ''}`}>
                      <button
                        type="button"
                        className="me-disclosure__toggle"
                        aria-expanded={isPayExtrasOpen}
                        aria-controls="me-pay-extras"
                        onClick={() => setIsPayExtrasOpen((open) => !open)}
                      >
                        <span className="me-field__label">PATO and Retirement</span>
                      </button>
                      {/* This year's Used and Remaining stay in view while the
                          section is closed; opening it hands over to the full
                          balance, which shows the same cards. */}
                      <div className="me-disclosure__peek" aria-hidden={isPayExtrasOpen}>
                        <div className="me-disclosure__peekinner">
                          <div className="me-disclosure__peekrow">
                            <PatoUsageSummary employeeId={employee.id} />
                            <RetirementUsageSummary employeeId={employee.id} />
                          </div>
                        </div>
                      </div>
                      <div className="me-disclosure__panel" id="me-pay-extras" inert={!isPayExtrasOpen}>
                        <div className="me-disclosure__inner">
                          {/* PATO is a balance the company keeps rather than an
                              election, so an employee sees theirs but cannot
                              adjust it - adding and voiding adjustments is what
                              `canManage` withholds.
                              Reading it is the point: it is their allowance, and
                              having to ask what it is for is friction with
                              nothing behind it. */}
                          <PatoBalance
                            employeeId={employee.id}
                            canManage={canManageAllEditingEmployee}
                          />
                          <div className="me-divider" />
                          <RetirementElection
                            employeeId={employee.id}
                            canManage={canManageRetirement}
                            isSelf={isOwnProfile}
                          />
                          {canViewPatoReport ? (
                            <>
                              <div className="me-divider" />
                              <button
                                type="button"
                                className="bdc-btn bdc-btn--sm"
                                onClick={() => setIsPatoReportOpen(true)}
                              >
                                <i className="bi bi-calendar2-week" aria-hidden="true" />
                                View PATO report
                              </button>
                            </>
                          ) : null}
                        </div>
                      </div>
                      {/* Centred under whatever is showing: the cards while
                          closed, the full section once open. */}
                      <div className="me-disclosure__morewrap">
                        <button
                          type="button"
                          className="me-disclosure__more"
                          aria-expanded={isPayExtrasOpen}
                          aria-controls="me-pay-extras"
                          onClick={() => setIsPayExtrasOpen((open) => !open)}
                        >
                          {isPayExtrasOpen ? 'See less' : 'See more'}
                          <i className="bi bi-chevron-down me-disclosure__chevron" aria-hidden="true" />
                        </button>
                      </div>
                    </div>
                    {canViewPatoReport ? (
                      <PatoReportModal
                        open={isPatoReportOpen}
                        onClose={() => setIsPatoReportOpen(false)}
                        employee={employee}
                      />
                    ) : null}
                  </>
                ) : null}
              </div>
            </section>
          </div>

          {/* ----------------------------------------------------- documents */}
          <section className="me-card">
            <div className="me-card__head">
              <h2 className="me-card__title">
                <i className="bi bi-folder2-open" aria-hidden="true" />
                Document Library
              </h2>
            </div>

            <div className="me-card__body">
              {isEditing && perms.documentLibrary ? (
                <>
                  <ProfileField
                    label="SharePoint library"
                    hint="Expense attachments are always stored in /Attachments."
                  >
                    <SharePointLocationPicker
                      value={{
                        sharepoint_drive_id: form.sharepointDriveId || null,
                        document_library_id: form.sharepointDriveId || null,
                      }}
                      onChange={(next) => setField(
                        'sharepointDriveId',
                        next?.sharepoint_drive_id || next?.document_library_id || ''
                      )}
                      disabled={saving}
                    />
                  </ProfileField>

                  <div className="me-divider" />
                </>
              ) : null}

              <div className="me-doclib">
                <DocumentLibraryBrowser employeeId={photoEmployeeId} />
              </div>
            </div>
          </section>
        </div>
      </div>

      {isEditing ? (
        <div className="me-editbar">
          <span className="me-editbar__status">
            <i className={`bi ${isDirty ? 'bi-pencil-square' : 'bi-info-circle'}`} aria-hidden="true" />
            {isDirty ? 'You have unsaved changes' : 'No changes yet'}
          </span>

          <div className="me-editbar__actions">
            <button type="button" className="bdc-btn" onClick={cancelEdit} disabled={saving}>
              Cancel
            </button>
            <button type="button" className="bdc-btn bdc-btn--primary" onClick={save} disabled={saving}>
              <i className="bi bi-check2" aria-hidden="true" />
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
